import { datadog } from '@flipster/utils/datadog';
import {
  getFlipsterConfigsV1Key,
  getFlipsterConfigsV2Key,
  getLocalStorage,
  setLocalStorage,
} from '@flipster/utils/localStorage';
import { OrderMarginType } from '@flipster/utils/types/base';

const DEFAULT_LEVERAGE = 1;
const DEFAULT_MARGIN_TYPE: OrderMarginType = 'Isolated';

// localStorage 값은 사용자가 임의로 조작할 수 있으므로 런타임에 직접 검증한다.
const isValidLeverage = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

const isValidMarginType = (value: unknown): value is OrderMarginType => value === 'Isolated' || value === 'Cross';

const asRecord = (value: unknown): Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

/**
 * 레거시 per-symbol 캐시(v1 leverage/marginType)를 v2로 1회성 마이그레이션한다.
 *
 * 배경: 주문 폼 마운트 시 사용자 액션 없이도 leverage(1x)/marginType(Isolated)가 캐시에 남아
 * user-level default 값을 가리는 문제가 있다. (Android와 동일한 이슈)
 *
 * 동작:
 * - v2 키가 이미 존재하면 마이그레이션된 것으로 보고 즉시 종료한다. (별도 flag 없이 v2 존재 = migrated)
 * - localStorage는 임의 조작될 수 있으므로 유효한 값만 채택한다. (그 외는 미설정으로 간주)
 * - 심볼별로 `marginType === Isolated && leverage === 1` 이면 기본값으로 보고 무시한다.
 *   값이 없거나 유효하지 않은 필드는 기본값으로 간주한다. (leverage→1, marginType→Isolated)
 * - 기본값이 아닌 심볼은 v1에 실제로 존재하던 유효한 값만 v2로 복사한다.
 * - v1 키는 삭제하지 않고 그대로 둔다. (롤백 안전성)
 *
 * 손상된 캐시 등으로 실패해도 주문 폼 진입을 막지 않도록 전체를 try/catch로 감싼다.
 */
export const migrateTradeDefaultsCache = (userId: number) => {
  try {
    const v2LeverageKey = getFlipsterConfigsV2Key(userId, 'leverage');
    const v2MarginTypeKey = getFlipsterConfigsV2Key(userId, 'marginType');

    // v2가 이미 있으면 마이그레이션 완료로 간주
    if (getLocalStorage(v2LeverageKey) != null || getLocalStorage(v2MarginTypeKey) != null) {
      return;
    }

    const v1LeverageRaw = getLocalStorage(getFlipsterConfigsV1Key(userId, 'leverage'));
    const v1MarginTypeRaw = getLocalStorage(getFlipsterConfigsV1Key(userId, 'marginType'));

    // 레거시 데이터가 없는 신규 유저는 마이그레이션할 것이 없음
    if (v1LeverageRaw == null && v1MarginTypeRaw == null) {
      return;
    }

    const v1Leverage = asRecord(v1LeverageRaw);
    const v1MarginType = asRecord(v1MarginTypeRaw);

    const symbols = new Set([...Object.keys(v1Leverage), ...Object.keys(v1MarginType)]);

    const migratedLeverage: Record<string, number> = {};
    const migratedMarginType: Record<string, OrderMarginType> = {};

    symbols.forEach((symbol) => {
      // 유효하지 않은 값은 미설정(undefined)으로 취급한다.
      const leverage = isValidLeverage(v1Leverage[symbol]) ? v1Leverage[symbol] : undefined;
      const marginType = isValidMarginType(v1MarginType[symbol]) ? v1MarginType[symbol] : undefined;

      // undefined는 기본값으로 간주하여 (1x, Isolated) 여부를 판정한다.
      const isDefault =
        (marginType ?? DEFAULT_MARGIN_TYPE) === DEFAULT_MARGIN_TYPE &&
        (leverage ?? DEFAULT_LEVERAGE) === DEFAULT_LEVERAGE;
      if (isDefault) return;

      // 실제로 존재했던 유효한 값만 복사한다.
      if (leverage !== undefined) migratedLeverage[symbol] = leverage;
      if (marginType !== undefined) migratedMarginType[symbol] = marginType;
    });

    // v1 데이터가 있었던 유저는 v2 키를 기록해 마이그레이션 완료를 표시한다. (재실행 방지)
    setLocalStorage(v2LeverageKey, migratedLeverage);
    setLocalStorage(v2MarginTypeKey, migratedMarginType);
  } catch (error) {
    // 손상된 캐시 등으로 마이그레이션이 실패해도 주문 폼 진입을 막지 않는다. 에러는 Datadog으로 보고한다.
    datadog.addError(error, { source: 'migrateTradeDefaultsCache', userId });
  }
};
